import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { GasRoute, GasRouteRequestBody } from '@/features/gasRoutes/types';
import { infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { FiltersGrid, Pagination } from '../types';

export const gasRoutesApi = createApi({
  reducerPath: 'gasRoutesApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/gas-routes`,
  }),
  tagTypes: ['GasRoutes', 'GasRoute'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getGasRoutesGridify: builder.query<GasRoute[], FiltersGrid | void>({
      query: (params = {}) => {
        return {
          url: `/query`,
          params,
        };
      },
      providesTags: () => ['GasRoutes'],
    }),
    getGasRoutesGridifyI: builder.infiniteQuery<
      GasRoute[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => {
        return {
          url: `/query`,
          params: { ...queryArg, ...pageParam },
        };
      },
      providesTags: () => ['GasRoutes'],
    }),
    getGasRouteById: builder.query<GasRoute, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: 'GasRoute', id }],
    }),
    createGasRoute: builder.mutation<string, GasRouteRequestBody>({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['GasRoutes'],
    }),
    updateGasRoute: builder.mutation<GasRoute, GasRouteRequestBody>({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_, __, payload) => [
        'GasRoutes',
        { type: 'GasRoute', id: payload.id },
      ],
    }),
    deleteGasRoute: builder.mutation<GasRoute, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: result => [
        'GasRoutes',
        { type: 'GasRoute', id: result?.id },
      ],
    }),
  }),
});

export const {
  useGetGasRouteByIdQuery,
  useCreateGasRouteMutation,
  useUpdateGasRouteMutation,
  useDeleteGasRouteMutation,
  useGetGasRoutesGridifyQuery,
  useLazyGetGasRoutesGridifyQuery,
} = gasRoutesApi;

export const useGetGasRoutesGridifyInfiniteQuery = normalizeInfiniteQueryData(
  gasRoutesApi.useGetGasRoutesGridifyIInfiniteQuery,
);
